import { useEffect, useCallback, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { usePageLayoutResize } from '@atlassian/jira-navigation-system/src/index.tsx';
import { NAVIGATION_UI_STORAGE_KEY } from '@atlassian/jira-router-resources-navigation-ui-state/src/constants.tsx';
import { navigationUiStateResource } from '@atlassian/jira-router-resources-navigation-ui-state/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { useResource } from '@atlassian/react-resource-router';
import { useLayoutStoreActions, DEFAULT_LEFT_SIDEBAR_WIDTH } from '../../store/index.tsx';

import { useLeftSidebarState } from '../left-sidebar-controller/index.tsx';
import { useRightSidebarState } from '../right-sidebar-controller/index.tsx';
import type { RightSidebarState } from '../../../../common/types.tsx';
import { setRemoteLayoutState } from './set-remote-layout-state/index.tsx';

const LayoutRemoteStateUpdaterOld = () => {
	// This prevents eccessive state updates on page loads
	const [initialLoad, setInitialLoad] = useState(true);
	const [localLeftSidebarWidthState, setLocalLeftSidebarWidthState] = useState<number>();
	const [localLeftSidebarCollapsedState, setLocalLeftSidebarCollapsedState] = useState<boolean>();
	const [localRightSidebarState, setLocalRightSidebarState] = useState<RightSidebarState>();

	// left-sidebar
	const { leftSidebarState } = usePageLayoutResize();
	const { isFlyoutOpen, isLeftSidebarCollapsed, leftSidebarWidth } = leftSidebarState;
	const resolvedLeftSidebarWidth: number = Math.max(leftSidebarWidth, DEFAULT_LEFT_SIDEBAR_WIDTH);
	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const isCollapsed: boolean = leftSidebarState.isLeftSidebarCollapsed;

	// right-sidebar
	const rightSidebarState = useRightSidebarState();
	const accountId = useAccountId();
	const { data, loading, error } = useResource(navigationUiStateResource);
	const isLayoutStateReady = leftSidebarWidth !== 0 && leftSidebarState.lastLeftSidebarWidth !== 0;

	// compares local vs external state coming via chain of callbacks
	// to determine if we really need to send an update of layout state
	const sideBarChanged = useCallback(
		(
			externalLeftSidebarWidth: number,
			externalIsLeftSidebarCollapsed: boolean,
			externalRightSidebarState: RightSidebarState,
		): boolean => {
			let changeDetected = false;
			if (!isEqual(externalLeftSidebarWidth, localLeftSidebarWidthState)) {
				setLocalLeftSidebarWidthState(externalLeftSidebarWidth);
				changeDetected = true;
			}
			if (!isEqual(externalIsLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
				setLocalLeftSidebarCollapsedState(externalIsLeftSidebarCollapsed);
				changeDetected = true;
			}
			if (!isEqual(externalRightSidebarState, localRightSidebarState)) {
				setLocalRightSidebarState(externalRightSidebarState);
				changeDetected = true;
			}
			return changeDetected;
		},
		[localLeftSidebarCollapsedState, localLeftSidebarWidthState, localRightSidebarState],
	);

	useEffect(() => {
		if (!isLayoutStateReady) return;
		if (error) return;
		setLeftSidebarState({
			width: resolvedLeftSidebarWidth,
			isCollapsed,
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			isFlyoutOpen: isFlyoutOpen as boolean,
			isVisible: true,
		});
	}, [
		error,
		isCollapsed,
		isFlyoutOpen,
		isLayoutStateReady,
		resolvedLeftSidebarWidth,
		setLeftSidebarState,
	]);
	const updateData = useCallback(async () => {
		if (loading) return;
		if (error) return;
		if (!isLayoutStateReady) return;
		if (!sideBarChanged(resolvedLeftSidebarWidth, isLeftSidebarCollapsed, rightSidebarState))
			return;
		const remoteData = {
			...data,
			leftSidebar: {
				width: resolvedLeftSidebarWidth,
				isCollapsed: isLeftSidebarCollapsed,
			},
			rightSidebar: rightSidebarState,
		};
		if (accountId) {
			try {
				await setUserProperties(accountId, NAVIGATION_UI_STORAGE_KEY, JSON.stringify(remoteData));
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (e: any) {
				fireErrorAnalytics({
					error: e,
					meta: {
						id: 'updateServerLayoutState',
						packageName: 'jiraLayoutController',

						teamName: 'magma',
					},
				});
			}
		}
	}, [
		accountId,
		data,
		error,
		isLayoutStateReady,
		isLeftSidebarCollapsed,
		loading,
		resolvedLeftSidebarWidth,
		rightSidebarState,
		sideBarChanged,
	]);
	useEffect(() => {
		if (initialLoad) {
			setInitialLoad(false);
			setLocalLeftSidebarWidthState(resolvedLeftSidebarWidth);
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
			setLocalRightSidebarState(rightSidebarState);
		} else {
			updateData();
		}
	}, [
		isLeftSidebarCollapsed,
		resolvedLeftSidebarWidth,
		updateData,
		rightSidebarState,
		initialLoad,
	]);
	return null;
};

const LayoutRemoteStateUpdaterNew = () => {
	const accountId = useAccountId();
	const leftSidebar = useLeftSidebarState();
	const rightSidebar = useRightSidebarState();

	const isLeftSidebarCollapsed = leftSidebar.isCollapsed;
	const leftSidebarWidth = leftSidebar.width;
	// This prevents eccessive state updates on page loads
	const [initialLoad, setInitialLoad] = useState(true);
	const [localLeftSidebarWidthState, setLocalLeftSidebarWidthState] = useState<number>();
	const [localLeftSidebarCollapsedState, setLocalLeftSidebarCollapsedState] = useState<boolean>();
	const [localRightSidebarState, setLocalRightSidebarState] = useState<RightSidebarState>();

	// compares local vs external state coming via chain of callbacks
	// to determine if we really need to send an update of layout state
	const sideBarChanged = useCallback(
		(
			externalLeftSidebarWidth: number,
			externalIsLeftSidebarCollapsed: boolean,
			externalRightSidebarState: RightSidebarState,
		): boolean => {
			let changeDetected = false;
			if (!isEqual(externalLeftSidebarWidth, localLeftSidebarWidthState)) {
				setLocalLeftSidebarWidthState(externalLeftSidebarWidth);
				changeDetected = true;
			}
			if (!isEqual(externalIsLeftSidebarCollapsed, localLeftSidebarCollapsedState)) {
				setLocalLeftSidebarCollapsedState(externalIsLeftSidebarCollapsed);
				changeDetected = true;
			}
			if (!isEqual(externalRightSidebarState, localRightSidebarState)) {
				setLocalRightSidebarState(externalRightSidebarState);
				changeDetected = true;
			}
			return changeDetected;
		},
		[localLeftSidebarCollapsedState, localLeftSidebarWidthState, localRightSidebarState],
	);

	useEffect(() => {
		if (!accountId) {
			return;
		}
		if (initialLoad) {
			setInitialLoad(false);
			setLocalLeftSidebarWidthState(leftSidebarWidth);
			setLocalLeftSidebarCollapsedState(isLeftSidebarCollapsed);
			setLocalRightSidebarState(rightSidebar);
			return;
		}
		if (!sideBarChanged(leftSidebarWidth, isLeftSidebarCollapsed, rightSidebar)) return;

		setRemoteLayoutState({
			accountId,
			state: {
				rightSidebar,
				leftSidebar: {
					isCollapsed: isLeftSidebarCollapsed,
					width: leftSidebarWidth,
				},
			},
		});
	}, [
		accountId,
		isLeftSidebarCollapsed,
		leftSidebarWidth,
		rightSidebar,
		initialLoad,
		sideBarChanged,
	]);

	return null;
};

export const LayoutRemoteStateUpdater = componentWithCondition(
	getWillShowNav4,
	LayoutRemoteStateUpdaterNew,
	LayoutRemoteStateUpdaterOld,
);
